import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase/config';

export interface NameTranslationResult {
  firstNameAr: string;
  lastNameAr: string;
  firstNameEn: string;
  lastNameEn: string;
}

class NameTranslationService {
  private static instance: NameTranslationService;

  private constructor() {}

  static getInstance(): NameTranslationService {
    if (!NameTranslationService.instance) {
      NameTranslationService.instance = new NameTranslationService();
    }
    return NameTranslationService.instance;
  }

  // Calls the `aiTranslateName` Cloud Function — a narrow, unauthenticated-safe
  // proxy (single short string in/out, prompt built server-side, key never
  // leaves the function). No client-side provider key handling here anymore.
  private async transliterate(name: string): Promise<string> {
    try {
      const aiTranslateName = httpsCallable<{ name: string }, { name: string }>(functions, 'aiTranslateName');
      const result = await aiTranslateName({ name });
      return result.data?.name || '';
    } catch (error) {
      console.error('Error transliterating name:', error);
      return '';
    }
  }

  /**
   * Translate a person's name from English to Arabic
   * Uses transliteration for proper names (not literal translation)
   */
  async translateToArabic(
    firstNameEn: string,
    lastNameEn: string
  ): Promise<{ firstNameAr: string; lastNameAr: string }> {
    try {
      const [firstNameAr, lastNameAr] = await Promise.all([
        this.transliterate(firstNameEn),
        this.transliterate(lastNameEn),
      ]);
      return {
        firstNameAr: firstNameAr || firstNameEn,
        lastNameAr: lastNameAr || lastNameEn,
      };
    } catch (error) {
      console.error('Error translating name to Arabic:', error);
      return { firstNameAr: firstNameEn, lastNameAr: lastNameEn };
    }
  }

  /**
   * Translate a person's name from Arabic to English
   * Uses transliteration for proper names (not literal translation)
   */
  async translateToEnglish(
    firstNameAr: string,
    lastNameAr: string
  ): Promise<{ firstNameEn: string; lastNameEn: string }> {
    try {
      const [firstNameEn, lastNameEn] = await Promise.all([
        this.transliterate(firstNameAr),
        this.transliterate(lastNameAr),
      ]);
      return {
        firstNameEn: firstNameEn || firstNameAr,
        lastNameEn: lastNameEn || lastNameAr,
      };
    } catch (error) {
      console.error('Error translating name to English:', error);
      return { firstNameEn: firstNameAr, lastNameEn: lastNameAr };
    }
  }

  /**
   * Auto-translate names based on the source language
   * Detects if the name is in Arabic and translates accordingly
   */
  async autoTranslate(
    firstName: string,
    lastName: string
  ): Promise<NameTranslationResult> {
    // Check if the name contains Arabic characters
    const containsArabic = /[\u0600-\u06FF]/.test(firstName) || /[\u0600-\u06FF]/.test(lastName);
    
    if (containsArabic) {
      // Name is in Arabic, translate to English
      const { firstNameEn, lastNameEn } = await this.translateToEnglish(firstName, lastName);
      return {
        firstNameAr: firstName,
        lastNameAr: lastName,
        firstNameEn,
        lastNameEn,
      };
    } else {
      // Name is in English/Latin, translate to Arabic
      const { firstNameAr, lastNameAr } = await this.translateToArabic(firstName, lastName);
      return {
        firstNameEn: firstName,
        lastNameEn: lastName,
        firstNameAr,
        lastNameAr,
      };
    }
  }
}

export const nameTranslationService = NameTranslationService.getInstance();
